import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePlatform } from "@/context/PlatformContext";
import { toast } from "sonner";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import Sidebar from "@/components/layout/Sidebar";
import TopBar from "@/components/layout/TopBar";
import MfaGraceBanner from "@/components/layout/MfaGraceBanner";
import UndoImportBanner from "@/components/layout/UndoImportBanner";
import MobileHeader from "@/components/mobile/MobileHeader";
import MobileNavDrawer from "@/components/mobile/MobileNavDrawer";
import EmergencyPauseBanner from "@/components/layout/EmergencyPauseBanner";
import BusyHourNotice from "@/components/layout/BusyHourNotice";

/**
 * App chrome: sidebar + banners + top bar + main content area.
 * Iter-60: hides the persistent sidebar below `md:` and swaps in a
 * hamburger + slide-over drawer. Desktop layout is byte-identical.
 * Delegates rendering to the smaller pieces in `components/layout/`.
 */
export default function Layout({ children }) {
  const { user, org, logout, hasPerm } = useAuth();
  const platform = usePlatform();
  const nav = useNavigate();
  const loc = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    toast.success("Signed out");
    nav("/login", { replace: true });
  };

  // Close the drawer on every route change.
  React.useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);

  return (
    <div className="min-h-screen md:grid overflow-x-hidden" style={{ gridTemplateColumns: "260px 1fr" }}>
      {/* Desktop sidebar — hidden on mobile. */}
      <div className="hidden md:block">
        <Sidebar
          user={user}
          org={org}
          platform={platform}
          hasPerm={hasPerm}
          onSignOut={doLogout}
        />
      </div>
      {/* Mobile-only slide-over that reuses the SAME Sidebar component. */}
      <MobileNavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Sidebar
          user={user}
          org={org}
          platform={platform}
          hasPerm={hasPerm}
          onSignOut={doLogout}
        />
      </MobileNavDrawer>

      <div className="flex flex-col min-w-0">
        <MobileHeader
          onOpenNav={() => setDrawerOpen(true)}
          title={platform.short_name || platform.name || "LeadFlow"}
        />
        <ImpersonationBanner />
        <EmergencyPauseBanner platform={platform} user={user} />
        <BusyHourNotice user={user} />
        <MfaGraceBanner user={user} />
        <UndoImportBanner />
        <TopBar user={user} org={org} />
        <main className="flex-1 p-4 md:p-6 min-w-0 overflow-x-hidden">{children}</main>
      </div>
    </div>
  );
}
